import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { CosmosTable } from '../models/cosmos/cosmos-table';
import { CosmosTableRow } from '../models/cosmos/cosmos-table-row';
import { CosmosTableSchema } from '../models/cosmos/cosmos-table-schema';
import { TableData } from '../models/table-data';
import { TenantService } from './tenant.service';
import { AuthService } from './auth-service.service';
import { ConfigService } from './config.service';
import { MyTableChanges } from '../models/my-table-changes';
import moment from 'moment';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class TableService {
  private readonly logger = inject(LoggerService);
  public $tables: BehaviorSubject<CosmosTable[]> = new BehaviorSubject<CosmosTable[]>(null);
  private tables: CosmosTable[] = [];
  private tableDataSets: TableData[] = [];
  public selectedTable;
  private clientNameSpace;
  private baseUrl: string;
  private page: number = 1;
  private pageCount: number = 25;
  public totalPages: number = 0;
  public rowChanges: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public rowChangesToDelete: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public rowChangesToCopy: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  myTableChanges: MyTableChanges;
  private tableIsValid: boolean = true;

  constructor(public tenantService: TenantService, private http: HttpClient, private authService: AuthService, private configService: ConfigService) {
    this.configService.$configuration.subscribe(config => {
      if (config != null) {
        this.baseUrl = config.servicesUrl;
      }
    });
    this.authService.$userClaims.subscribe(claims => {
      if (claims != null && claims.length > 0) {
        var clientNameSpace = claims.find(claim => claim.type == "df.ns").value;
        this.clientNameSpace = clientNameSpace;
      }
    });
  }

  public async loadTables() {
    var schemaIds = [];
    var schemaIdCheck = {};

    if (this.tableDataSets.length == 0) {
      await this.loadTableDataSets();
    }
    this.tables = this.tableDataSets.map(t => new CosmosTable(t));
    this.tables.forEach(t => {
      if (!schemaIdCheck[t.schemaId]) {
        schemaIds.push(t.schemaId);
        schemaIdCheck[t.schemaId] = true;
      }
    });
    this.tables = this.tables.sort(this.tableSortFunction);
    this.$tables.next(this.tables);
  }

  public selectTable(tableId: string) {
    if (this.tables != null) {
      this.selectedTable = this.tables.find(t => t.id == tableId);
      this.page = 1;
    }
  }

  public resetChangeTracking(tableId: string): void {
    this.myTableChanges = new MyTableChanges(tableId);
    this.rowChanges.next([]);
    this.rowChangesToDelete.next([]);
    this.rowChangesToCopy.next([]);
  }

  public hasPendingChanges(tableId?: string): boolean {
    const c = this.myTableChanges;
    if (!c) return false;
    if (tableId && c.tableId !== tableId) return false;
    return !!c.editedRows?.rows?.currentChanges && c.editedRows.rows.currentChanges.size > 0;
  }

  public ensureTracking(tableId: string): void {
    if (!this.myTableChanges || this.myTableChanges.tableId !== tableId) {
      this.resetChangeTracking(tableId);
      this.defaultRegistry.clear();
    }
  }
  private async loadTableDataSets(nameSpaceOverride: string = null) {
    var tableIdsUrl = `${this.baseUrl}/api/${nameSpaceOverride != null ? nameSpaceOverride : this.clientNameSpace}/TableData`;
    await this.http.get<TableData[]>(tableIdsUrl).toPromise().then(async tables => {
      this.tableDataSets = tables
    })
      .catch(e => {
        this.logRequestError('loadTableDataSets', tableIdsUrl, e);
      });
  }

  tableSortFunction = (a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    const aMissing = a.order === null || a.order === undefined;
    const bMissing = b.order === null || b.order === undefined;

    // Both missing -> alpha
    if (aMissing && bMissing) {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    }

    // One missing -> missing goes last
    if (aMissing !== bMissing) {
      return aMissing ? 1 : -1;
    }

    // Both have order -> numeric, then alpha
    if (a.order === b.order) {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    }

    return a.order < b.order ? -1 : 1;
  };

  public async loadSelectedTable() {
    if (this.selectedTable != null) {
      // ---LOAD SCHEMA---
      await this.loadTableSchema(this.selectedTable.schemaId);

      // // ---LOAD ROWS---
      // //Load first page of rows with size 25
      const tableData = await this.loadTablePage(this.selectedTable.id, this.page, this.pageCount, null, null);
      this.updateTableData(tableData);
    }
  }

  public async loadTableSchema(schemaId: string) {
    //Build URL to load schema
    var schemasUrl = `${this.baseUrl}/api/${this.clientNameSpace}/TableSchema/${schemaId}`;
    //HTTP GET schema
    await this.http.get<CosmosTableSchema>(schemasUrl).toPromise().then(schema => {
      this.selectedTable.schema = schema
    }).catch(e => {
      this.logRequestError('loadTableSchema', schemasUrl, e);
    });
  }

  public updateTableData(tableData: TableData) {
    this.selectedTable.data.id = tableData.id;
    this.selectedTable.data.itemType = tableData.id;
    this.selectedTable.data.name = tableData.name;
    this.selectedTable.data.schemaId = tableData.schemaId;
    this.selectedTable.data.partitionKey = tableData.partitionKey;
    this.selectedTable.data.rows = [];
    tableData.rows.forEach(row => {
      var rowData = new CosmosTableRow();
      rowData.currentValue = row;
      rowData.initialValue = row;
      this.selectedTable.data.rows.push(rowData);
    });
  }

  public addTableRow(rowData: any, tableId: string) {
    var table = this.tables.find(t => t.id == tableId);
    var newRow = new CosmosTableRow();
    newRow.currentValue = rowData;
    table.data.rows.push(newRow);
  }

  public addCopyTableRow(rowData: any, tableId: string) {
    var table = this.tables.find(t => t.id == tableId);
    var newRow = new CosmosTableRow();
    newRow.currentValue = rowData;
    newRow.initialValue = rowData;
    this.myTableChanges.editRow(newRow.currentValue);
    table.data.rows.push(newRow);
  }

  public editRow(rowData: any) {
    this.myTableChanges.editRow(rowData);
  }


  public getInitialValues(rowId: string, tableId: string) {
    var row = this.myTableChanges.editedRows.getRow(rowId);
    return row;
  }


  public async importTableRowsFromFile(rows: {}[], tableId: string): Promise<boolean> {
    var ret = false;
    var table = this.tables.find(t => t.id == tableId);
    var body = {};
    body["id"] = table.data.id;
    body["schemaId"] = table.data.schemaId;
    body["name"] = table.data.name;
    body["rows"] = rows;
    var upsertUrl = `${this.baseUrl}/api/${this.tenantService.$tenant.value.id}/${tableId}`;
    await this.http.put<TableData>(upsertUrl, body).toPromise().then(tableData => {
      // update $tables
      rows.forEach(updatedRow => {
      });
      ret = true;
    }).catch(e => {
      return false;
    });
    return ret;
  }

  public async updateTableRows(tableDataId: string): Promise<boolean> {
    if (this.myTableChanges?.tableId && this.myTableChanges.tableId !== tableDataId) {
      return true;
    }
    let ret = false;

    const table = this.tables.find(t => t.id === tableDataId);
    if (!table) return false;

    const schema: CosmosTableSchema = table.schema;

    const body: any = {
      id: table.data.id,
      partitionKey: table.data.partitionKey,
      schemaId: table.data.schemaId,
      name: table.data.name,
      rows: [] as any[],
    };
    const pk = `${table.data.partitionKey}|${body.id}`;

    const changesMap = this.myTableChanges?.editedRows?.rows?.currentChanges as Map<string, any> | undefined;
    const changes: any[] = changesMap ? Array.from(changesMap.values()) : [];

    for (const row of changes) {
      const isGridRow = !!(row?._row && row._row.data && row._row.data.values);
      if (!isGridRow) {
        const rowId = row.id;
        const defaultSet = this.getDefaultSet(rowId) || new Set<string>();
        const isCreate = row.rowState === 2;

        // Skip untouched originals
        if (row.rowState === 1) continue;

        const newRow: any = { partitionKey: pk };
        ['id', 'rowState'].forEach(k => {
          if (Object.prototype.hasOwnProperty.call(row, k)) newRow[k] = row[k];
        });
        if (isCreate && Object.prototype.hasOwnProperty.call(row, 'DISTransactionId')) {
          newRow.DISTransactionId = row.DISTransactionId;
        }

        schema.columns.forEach(c => {
          let v = Object.prototype.hasOwnProperty.call(row, c.columnName) ? row[c.columnName] : null;

          const equalsDef = this.equalsDefault(c, v);
          const isMarkedDefault = defaultSet.has(c.columnName);
          // Strip defaults for Both creates and updates so they aren't persisted
          if (isMarkedDefault || equalsDef) {
            v = null;
          }

          const isNumeric = c.columnDataType === 2 || c.columnDataType === 3;
          const isString = c.columnDataType === 1;
          const isDate = c.columnDataType === 5;
          if (isNumeric && (v === '' || v === undefined)) v = null;
          if (isString && typeof v === 'string' && v.trim() === '') v = null;
          if (isDate && (v === '' || v === undefined)) v = null;

          newRow[c.columnName] = v;
        });

        body.rows.push(newRow);
        continue;
      }

      const values = row._row.data.values;
      const rowId = values['id'];
      const defaultSet = this.getDefaultSet(rowId) || new Set<string>();
      const isCreate = values['rowState'] === 2;

      // Skip untouched originals
      if (values['rowState'] === 1) continue;

      const updatedRow: any = {
        rowState: values['rowState'],
        id: values['id'],
        partitionKey: pk,
        ...(isCreate && { DISTransactionId: values['DISTransactionId'] ?? null }),
      };

      schema.columns.forEach(c => {
        if (Object.prototype.hasOwnProperty.call(values, c.columnName)) {
          let v = values[c.columnName];
          const equalsDef = this.equalsDefault(c, v);
          const isMarkedDefault = defaultSet.has(c.columnName);
          // Strip defaults for both creates and updates so they aren't persisted
          if (isMarkedDefault || equalsDef) {
            v = null;
          }

          const isNumeric = c.columnDataType === 2 || c.columnDataType === 3;
          const isString = c.columnDataType === 1;
          const isDate = c.columnDataType === 5;
          if (isNumeric && (v === '' || v === undefined)) v = null;
          if (isString && typeof v === 'string' && v.trim() === '') v = null;
          if (isDate && (v === '' || v === undefined)) v = null;

          updatedRow[c.columnName] = v;
        }
      });
      body.rows.push(updatedRow);
    }
    if (!body.rows.length) {
      if (this.hasPendingChanges(tableDataId)) {
        this.resetChangeTracking(tableDataId);
      }
      return true;
    }
    const upsertUrl = `${this.baseUrl}/api/${this.clientNameSpace}/TableData/${tableDataId}`;

    try {
      const tableData = await this.http.put<any>(upsertUrl, body).pipe(first()).toPromise();
      const rawSuccessIds = tableData?.createItems?.successIds ?? {};
      const idMap: Record<string, string> = {};
      for (const [realId, tempId] of Object.entries(rawSuccessIds as Record<string,string>)) {
        if (tempId) idMap[tempId] = realId;
      }
      if (table?.data?.rows?.length) {
        table.data.rows.forEach(localRow => {
          const txnId = localRow.currentValue?.DISTransactionId;
          if (txnId && idMap[txnId]) {
            localRow.currentValue.id = idMap[txnId];
            delete localRow.currentValue.DISTransactionId;
          }
        });
      }
      this.updateRowReferencesAfterSave(idMap);

      if (tableData?.rows?.length) {
        tableData.rows.forEach((returnedRow: any) => {
          const txnId = returnedRow?.DISTransactionId;
          if (txnId && idMap[txnId]) {
            returnedRow.id = idMap[txnId];
          }
        });
      }

      ret = !(tableData?.failures > 0);
    } catch (error) {
      this.logger.error('TableService', 'Failed to save table data', error, {
        operation: 'updateTableRows',
        endpointPath: upsertUrl,
        tableDataId
      });
      ret = false;
    }
    if (ret) {
      this.resetChangeTracking(tableDataId);
    }
    return ret;
  }

  public async loadTablePage(
    tableId: string,
    page: number,
    size: number,
    filter: string,
    sort: string
  ) {
    this.page = page;
    this.pageCount = size;

    const qp =
      `page=${page}` +
      `&size=${size}` +
      `&filter=${encodeURIComponent(filter ?? '')}` +
      `&sort=${encodeURIComponent(sort ?? '')}`;

    const totalPageCountUrl =
      `${this.baseUrl}/api/${this.clientNameSpace}/TableData/${tableId}/count?${qp}`;

    const tableIdsUrl =
      `${this.baseUrl}/api/${this.clientNameSpace}/TableData/${tableId}/pages?${qp}`;

    try {
      // fetch total rows, compute total pages safely
      const totalRowCount = await this.http.get<number>(totalPageCountUrl).toPromise();
      this.totalPages = Math.max(1, Math.ceil((totalRowCount || 0) / this.pageCount));

      // fetch the page of rows
      const tableData = await this.http.get<TableData>(tableIdsUrl).toPromise();

      // overlay any unsaved edits from the client
      if (this.myTableChanges?.tableId === tableId && this.myTableChanges?.editedRows?.rows) {
        const edited = this.myTableChanges.editedRows.rows;
        edited.currentChanges.forEach((value, key) => {
          const idx = tableData.rows.findIndex(r => r.id === key);
          if (idx !== -1) {
            Object.assign(tableData.rows[idx], value);
          } else if (value.rowState === 2) {
            tableData.rows.push(value);
          }
        });
      }

      return tableData;
    } catch (error) {
      this.logger.error('TableService', 'Failed to load table page', error, {
        operation: 'loadTablePage',
        endpointPath: tableIdsUrl,
        tableId
      });
      return undefined;
    }
  }

  async downloadFile(tableId: string, alias: string, filterParams: string, currentCulture: string) {
    try {
      // Encode the filterParams
      const encodedFilterParams = encodeURIComponent(filterParams || "");

      // Construct the URL with the encoded filterParams
      const allRowsCsv = `${this.baseUrl}/api/${this.clientNameSpace}/TableData/${tableId}/csv?filter=${encodedFilterParams}`;
      var fileBlob: Blob = null;

      await this.http.get(allRowsCsv, {
        responseType: 'blob',
        headers: {
          'Current-Culture': currentCulture
        }
      }).toPromise()
        .then(c => { fileBlob = c })
        .catch(e => { this.logRequestError('downloadFile', allRowsCsv, e); });
      return fileBlob;
    } catch (error) {
      this.logger.error('TableService', 'Failed to download table file', error, {
        operation: 'downloadFile',
        endpointPath: `${this.baseUrl}/api/${this.clientNameSpace}/TableData/${tableId}/csv`,
        tableId,
        alias
      });
    }
  }

  private logRequestError(operation: string, url: string, error: unknown): void {
    this.logger.error('TableService', `${operation} failed`, error, {
      operation,
      endpointPath: url
    });
  }


  removeRowChange(rows: any[]) {
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    const currentRowChanges = this.rowChanges.value;
    const remainingRowChanges = currentRowChanges.filter(currentRow =>
      !rows.some(row => row && row._row && row._row.data && row._row.data.id === currentRow._row.data.id)
    );
    this.rowChanges.next(remainingRowChanges);
  }

  removeRowById(id: string | number) {
    const currentRowChanges = this.rowChanges.getValue();
    const remainingRowChanges = currentRowChanges.filter(row => row._row.data.id !== id);
    this.rowChanges.next(remainingRowChanges);
  }

  //Add row to rowChanges
  addRowChange(newRow: any) {
    let currentRowChanges = this.rowChanges.value;
    currentRowChanges = currentRowChanges.filter(rowChange => rowChange !== null);
    const isUnique = !currentRowChanges.some(existingRow =>
      existingRow && existingRow._row.data.id === newRow._row.data.id);
    if (isUnique) {
      this.rowChanges.next([...currentRowChanges, newRow]);
    }
  }

  addRowToRowChangesToDelete(rows: any[], tableId: string) {
    // If rows is an object, convert it to an array
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    const currentRowChanges = this.rowChangesToDelete.value;
    // Filter out rows that are already present in rowChanges
    const uniqueRows = rows.filter(row =>
      !currentRowChanges.some(existingRow => existingRow._row.data.id === row._row.data.id)
    );
    const newRowChanges = [...currentRowChanges, ...uniqueRows];
    this.rowChangesToDelete.next(newRowChanges);
    rows.forEach(row => {
      if (row && row._row && row._row.data) {
        row._row.data.rowState = 4;
      }
    });
    // Add rows to myTableChanges
    rows.forEach(row => {
      if (row) { 
        this.myTableChanges.editRow(row._row.data);
      }
    });
  }

  private updateRowReferencesAfterSave(idMap: { [oldTxnId: string]: string }) {
    const rowMaps = this.myTableChanges?.editedRows?.rows;
    if (!rowMaps) return;

    ['currentChanges', 'originalChange', 'previousChanges'].forEach(mapName => {
      const map = rowMaps[mapName] as Map<string, any> | undefined;
      if (!map || !(map instanceof Map)) return;
      Array.from(map.keys()).forEach((key: string) => {
        const newId = idMap[key];
        if (newId) {
          const value = map.get(key);
          if (value) {
            value.id = newId;
            map.set(newId, value);
            map.delete(key);
          }
        }
      });
    });

  [this.rowChanges, this.rowChangesToDelete, this.rowChangesToCopy].forEach(subject => {
    const updated = subject.value.map(row => {
      const oldId = row?._row?.data?.id;
      const newId = oldId && idMap[oldId];
      if (newId) {
        row._row.data.id = newId;
      }
      return row;
    });
    subject.next(updated);
  });

  const reg: Map<string, Set<string>> | undefined = (this as any).defaultRegistry;
  if (reg && reg instanceof Map) {
    const remapped = new Map<string, Set<string>>();
    reg.forEach((set, oldKey) => {
      const newKey = idMap[oldKey] || oldKey;
      const acc = remapped.get(newKey) ?? new Set<string>();
      set.forEach(f => acc.add(f));
      remapped.set(newKey, acc);
    });
    (this as any).defaultRegistry = remapped;
  }
}

  public get isTableValid(): boolean {
  return this.tableIsValid;
}
  public setTableValidState(isValid: boolean) {
    this.tableIsValid = isValid;
  } 

  private defaultRegistry = new Map<string, Set<string>>();

public markDefault(rowId: string, field: string): void {
  if (!rowId || !field) return;
  const set = this.defaultRegistry.get(rowId) ?? new Set<string>();
  set.add(field);
  this.defaultRegistry.set(rowId, set);
}
public clearDefault(rowId: string, field: string): void {
  const set = this.defaultRegistry.get(rowId);
  if (!set) return;
  set.delete(field);
  if (set.size === 0) this.defaultRegistry.delete(rowId);
}
public isDefault(rowId: string, field: string): boolean {
  const set = this.defaultRegistry.get(rowId);
  return !!set && set.has(field);
}
public getDefaultSet(rowId: string): Set<string> | undefined {
  return this.defaultRegistry.get(rowId);
}

public clearAllDefaults(): void {
  this.defaultRegistry.clear();
}

public parseDefaultForColumnPublic(col) { return this.parseDefaultForColumn(col); }
public equalsDefaultPublic(col, val) { return this.equalsDefault(col, val); }

private parseDefaultForColumn(col: { columnDataType: number; defaultValue?: any }) {
  const t = col.columnDataType;
  const raw = col.defaultValue;
  if (raw === undefined || raw === null || raw === "") return null;

  switch (t) {
    case 1: // string
      return String(raw);
    case 2: // integer
      return Number.isFinite(Number(raw)) ? Number(raw) : null;
    case 3: // decimal
      return Number.isFinite(Number(raw)) ? Number(raw) : null;
    case 4: // boolean
      if (typeof raw === "boolean") return raw;
      if (typeof raw === "string") return raw.toLowerCase() === "true";
      return Boolean(raw);
   case 5: { // date 
      const m = moment(String(raw).trim(), [
        "YYYY-MM-DDTHH:mm:ss","YYYY-MM-DD",
        "MM/DD/YYYY","MM-DD-YYYY","MM/DD/YY","MM-DD-YY",
        "MMDDYYYY","MMDDYY","YY-MM-DD","YYYY/MM/DD",
        "M/D/YY","M-D-YY","M/D/YYYY","M-D-YYYY"
      ], false);
      return m.isValid() ? m.startOf('day').format("YYYY-MM-DD[T]HH:mm:ss") : null;
    }
    default:
      return raw;
  }
}

private equalsDefault(col: { columnDataType: number; defaultValue?: any }, value: any): boolean {
  const t = col.columnDataType;
  const def = this.parseDefaultForColumn(col);
  if (def === null) return false;
  if (value === null || value === undefined || value === "") return false;

  switch (t) {
    case 1: { // string (trim)
      return String(value).trim() === String(def).trim();
    }
    case 2: // integer
    case 3: { // decimal
      const n = Number(value);
      const dn = Number(def);
      return Number.isFinite(n) && Number.isFinite(dn) && n === dn;
    }
    case 4: { // boolean
      return (typeof value === "boolean" ? value : String(value).toLowerCase() === "true") === (def as boolean);
    }
    case 5: { // date
      const m = moment(String(value).trim(), [
        "YYYY-MM-DDTHH:mm:ss","YYYY-MM-DD",
        "MM/DD/YYYY","MM-DD-YYYY","MM/DD/YY","MM-DD-YY",
        "MMDDYYYY","MMDDYY","YY-MM-DD","YYYY/MM/DD",
        "M/D/YY","M-D-YY","M/D/YYYY","M-D-YYYY"
      ], false);
      if (!m.isValid()) return false;
      return m.startOf('day').format("YYYY-MM-DD[T]HH:mm:ss") === def;
    }
    default:
      return value === def;
  }
}
}